/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
  ListFilterResponseProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface CpoConnectionLogsRequest extends ApiRequest {
  connectionId: string;
  startDate: string;
  endDate: string;
}

interface FetchCpoCommandsResponse {
  results: any[];
  limit: number;
  page: number;
  totalCount: number;
  totalPages: number;
}

/**
 * Fetches a page of OCPI commands received from connected eMSPs for a CPO role.
 */
export const fetchCpoConnectionLogs = createAsyncThunk<FetchCpoCommandsResponse, CpoConnectionLogsRequest>(
  'cpo-connection-logs/fetchCpoConnectionLogs',
  async (
    request,
    { dispatch, rejectWithValue },
  ) => {
    console.log(request);
    try {
      const response = await ocpiApi.getInternalCpoConnectionLog(request);
      return response?.data;
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.message || 'Failed to fetch received commands.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Fetches the filter list for the CPO connection logs table from the backend
 * and transforms the response into {@link CommonFilterProperties} using
 * {@link ConfigureListFilterResponse}, then prepends the local search filter.
 */
export const fetchCpoConnectionLogFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'cpo-connection-logs/fetchCpoConnectionLogFilters',
  async (
    _,
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await ocpiApi.getInternalCpoConnectionLogFilter({ role: 'CPO' });
      const apiFilters: ListFilterResponseProperties[] = response.data;
      const configuredFilters = ConfigureListFilterResponse(apiFilters);
      return [...availableFilters, ...configuredFilters];
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.message || 'Failed to fetch received commands.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

interface CPOConnectionLogsInitialState {
  connectionLogs: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

const initialState: CPOConnectionLogsInitialState = {
  connectionLogs: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const cpoConnectionLogSlice = createSlice({
  name: 'cpo-connection-logs',
  initialState,
  reducers: {
    /**
     * Update CPO Commands filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateCPOConnectionLogFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableCpoCommandsFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchCpoConnectionLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCpoConnectionLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.connectionLogs = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.totalCount;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchCpoConnectionLogs.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCpoConnectionLogFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchCpoConnectionLogFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchCpoConnectionLogFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateCPOConnectionLogFilters, disableCpoCommandsFilterApplied, setSortData } =
  cpoConnectionLogSlice.actions;

export default cpoConnectionLogSlice.reducer;
